<template>
  <audit-log
    :is-loading="isLoading"
    :title="`${notification.name} Notification Audit Log`"
    :entries="auditLog"
    :get-changes="getChanges"
    @close="$emit('close')"
  />
</template>

<script>
import { isEqual, intersection, difference } from 'lodash'
import { AuditLog } from '@/components/core/AuditLog'

const NA = 'N/A'

export default {
  name: 'NotificationEventAuditLogForm',
  components: {
    AuditLog,
  },
  props: {
    isLoading: Boolean,
    auditLog: {
      type: Array,
      required: true,
    },
    notification: {
      type: Object,
      required: true,
    },
  },
  emits: ['close'],
  methods: {
    getChanges(logEntry) {
      return this.compareNotificationEventAuditLogEntry(logEntry)
    },

    compareNotificationEventAuditLogEntry(logEntry) {
      const changes = []

      const pushEntry = (field, from, to) => changes.push(`Changed ${field} from ${from ?? NA} to ${to ?? NA}`)

      if (!Object.keys(logEntry.from).length) {
        changes.push('Created Notification')
        return changes
      }

      const { from, to } = logEntry

      for (const key of ['name', 'status', 'trigger']) {
        if (from[key] !== to[key]) {
          pushEntry(key, from[key], to[key])
        }
      }

      for (const key of ['type', 'mail_template_id']) {
        if (from.rules[key] !== to.rules[key]) {
          pushEntry(`rules.${key}`, from.rules[key], to.rules[key])
        }
      }

      for (const ttlType of ['ttl', 'global_ttl']) {
        const from = logEntry.from.rules[ttlType]
        const to = logEntry.to.rules[ttlType]

        const fmt = ttl => `${ttl.amount || NA} ${ttl.type}`
        if (
          from.type !== to.type ||
          from.amount !== to.amount
        ) {
          pushEntry(`rules.${ttlType}`, fmt(from), fmt(to))
        }
      }

      for (const recurringKey of ['period', 'amount', 'unit']) {
        const from = logEntry.from.rules.recurring[recurringKey]
        const to = logEntry.to.rules.recurring[recurringKey]

        if (from !== to) {
          pushEntry(`rules.recurring.${recurringKey}`, from || NA, to || NA)
        }
      }

      for (const availabilityKey of ['period', 'start_time', 'end_time', 'start_date', 'end_date']) {
        const from = logEntry.from.rules.availability[availabilityKey]
        const to = logEntry.to.rules.availability[availabilityKey]

        if (from !== to) {
          pushEntry(`rules.availability.${availabilityKey}`, from || NA, to || NA)
        }
      }

      if (!isEqual(from.rules.availability.days, to.rules.availability.days)) {
        const fmt = days => (days ?? [NA]).join(', ')
        pushEntry('rules.availability.days', fmt(from.rules.availability.days), fmt(to.rules.availability.days))
      }

      if (!isEqual(from.rules.filter, to.rules.filter)) {
        const fmtFilter = filter => `${filter.field} ${filter.equation} ${filter.value}`
        const fmtList = list => (list ?? []).map(fmtFilter).join(', ')

        pushEntry('rules.filter', fmtList(from.rules.filter), fmtList(to.rules.filter))
      }

      if (!isEqual(from.rules.custom_filter, to.rules.custom_filter)) {
        const fmtFilter = (key, config) => `${key}, ${config.equation} ${config.value}`
        const fmtList = filter => Object.keys(filter ?? {}).map(key => fmtFilter(key, filter[key])).join(', ')

        pushEntry('rules.custom_filter', fmtList(from.rules.custom_filter), fmtList(to.rules.custom_filter))
      }

      for (const templateLocation of ['base', 'toast', 'popup', 'url']) {
        changes.push(
          ...this.compareTranslations(from.template[templateLocation], to.template[templateLocation], templateLocation),
        )
      }

      if (changes.length === 0) {
        changes.push('No changes')
      }

      return changes
    },

    compareTranslations(from, to, recordName = 'base') {
      if (!from && !to) return [];
      if (!from) return [`${recordName} added`];
      if (!to) return [`${recordName} removed`];

      const changes = [];
      const fromLanguages = Object.keys(from);
      const toLanguages = Object.keys(to);

      const removed = difference(fromLanguages, toLanguages);
      const added = difference(toLanguages, fromLanguages);
      const common = intersection(fromLanguages, toLanguages);

      if (removed.length) {
        changes.push(
          removed.length === 1
            ? `Language ${removed[0]} was removed`
            : `Languages ${removed.join(", ")} were removed`,
        );
      }

      if (added.length) {
        changes.push(
          added.length === 1
            ? `Language ${added[0]} was added`
            : `Languages ${added.join(", ")} were added`,
        );
      }

      for (const lang of common) {
        for (const field of ['header', 'content', 'excerpt']) {
          const fromText = from[lang][field];
          const toText = to[lang][field];

          if (fromText !== toText) {
            if (!fromText && toText) {
              changes.push(`${lang} translation added`)
              continue
            }
            if (fromText && !toText) {
              changes.push(`${lang} translation removed`)
              continue
            }

            changes.push(
              `${lang} translation ${field} changed from ${fromText} to ${toText}`,
            );
          }
        }
      }

      return changes;
    }
  }
}
</script>
